import * as Mui from "@mui/material";
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Grid,
    IconButton,
    Stack,
    Tab,
    Tabs,
    Typography
} from "@mui/material";
import {useContext, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {SubmitHandler, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {useNavigate, useParams} from "react-router-dom";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import * as Const from "./components/filters/PaymentRequestConstants";
import {checkCorrelationID, checkCorrelationName, handleStatus} from "./components/filters/PaymentRequestConstants";
import {usePaymentRequestRepository} from "../../../api/repositories/PaymentRequestRepository";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import {formatCurrency} from "../../../utils/FormatUtils";
import useToggle from "./hooks/useToggle";
import EditPaymentRequest from "./EditPaymentRequest";
import EditAmount from "./EditAmount";
import {STATE_MODAL_UPDATING} from "./atoms/PaymentRequestAtoms";
import {useRecoilState} from "recoil";
import {useHasScope} from "../../../../../config/globalConfig";
import TabPanel from "../../../../finessecurity/component/TabPanel";
import CustomerRADetails from "./components/tabs/CustomerRADetails";
import CustomerBookingDetails from "./components/tabs/CustomerBookingDetails";
import FormTextField from "../../../experiments/forms/FormTextField";
import FormDatePicker from "../../../experiments/forms/FormDatePicker";
import dayjs from "dayjs";
import FormDateTimePicker from "../../../experiments/forms/FormDateTimePicker";
import {
    OUTSTANDING_ACCOUNTED,
    OUTSTANDING_ADD_AMOUNT,
    OUTSTANDING_ADD_NOTE,
    OUTSTANDING_EXTERNAL_MANAGEMENT,
    OUTSTANDING_STATUS,
    OUTSTANDING_SUSPEND
} from "../../../utils/FinanceScope";
import EditIcon from "@mui/icons-material/Edit";
import UnpaidHistory from "./UnpaidHistory";
import FinanceDialog from "../../../component/FinanceDialog";
import {InfoRounded} from "@mui/icons-material";
import * as React from "react";


function OutstandingManagementDetailWizard() {
    const { t } = useTranslation("finance", { keyPrefix: "payments.payment-request" });
    const { getPaymentsRequestsById, putPaymentsRequestsById, downloadXML } = usePaymentRequestRepository();
    const navigate = useNavigate();
    const hasScope = useHasScope
    const { id } = useParams();
    const [detail, setDetail] = useState<any>();
    const [showEditNotes, toggleEditNote] = useToggle();
    const [showEditAmount, toggleEditAmount] = useToggle();
    const { dispatchGlobalData } = useContext(GlobalContext);
    const [updating, setUpdating] = useRecoilState(STATE_MODAL_UPDATING);
    const schema = Yup.object().shape({ });
    const [activeTab, setActiveTab] = useState(0);
    const [openModal, setOpenModal] = useState(false);


    function allyProps(index: number) {
        return {
            id: `simple-tab-${index}`,
            'aria-controls': `simple-tabpanel-${index}`
        };
    }
    const handleChangeTab = (_e: any, newValue: number) => {
        setActiveTab(newValue);
    };

    useEffect(() => {
        if (id !== undefined) {
            getPaymentsRequestsById(id).then((response) => {
                let data = response.result;
                setDetail(data);
                setValue('paymentType', data?.type ? t(Const.convertTypology(Const.paymentTypes, data?.type)) : "");
                setValue('amount', data.amount ? formatCurrency(data.amount, "EUR", 0.01) : "");
                setValue('amountType', data?.amountType ? t(Const.convertAmountType(Const.amountTypes, data.amountType, data.usePreAuth)) : "");
                setValue('status', data?.status ? convertStatus(data) : "");
                setValue('dueDate', data.dueDate);
                setValue('modifiedDate', data.modifiedDate);
                setValue('id', data.id);
                setValue('invoiceDate', data.invoiceDate);
                setValue('sapInvoiceNumber', data.sapInvoiceNumber);
                setValue('sapReferenceId', data.sapReferenceId);
                setValue('invoiceNumber', data.invoiceNumber);
                setValue('customerId', data.customerId);
                setValue('customer', data.customerName);
                setValue('causal', getReason(data.reason));
                setValue('ra', data.raCode);
                setValue('paymentMode', data.cardTerminalType);
                setValue('termsOfPayment', data.invoiceSchema);
                setValue('cardNetwork', data.cardNetwork);
                setValue('cardTerminalId', data.cardTerminalId);
                setValue('serviceItem', data.serviceItems);
                setValue('note', data.note);
                setValue('maskeredPan', data.maskeredPan);
                setValue('lastTransactionId', data.lastTransactionId);
                setValue('lastTransactionDate', data.lastTransactionDate);
                setValue('amountRecovered', getAmountSummary(data));
                setValue('preauthAmountReleased', data.preauthAmountReleased ?  formatCurrency(data.preauthAmountReleased, "EUR", 0.01) : "");
                setValue('raDate', data.raDate);
                setValue('vehiclePlateCode', data.ntwRaData?.vehiclePlateCode);
                setValue('countyResidence', data.countyResidence);
                setValue('bookingDate', data.bookingDate);
                setValue('checkoutDmsIdentificationCode', data.checkoutDmsIdentificationCode);
                setValue('checkinDmsIdentificationCode', data.checkinDmsIdentificationCode);
                setValue('correlationName', checkCorrelationName({data, value: data.correlationName}, t));
                setValue('correlationId', checkCorrelationID({data, value: data.correlationId}, t));
            })
        }
        if (updating) setUpdating(false);
    }, [id, t, updating])

    function getAmountSummary(data: any) {
        let amount = data.amountRecovered ? formatCurrency(data.amountRecovered, "EUR", 0.01) : "";
        let amountSummary = amount ? (amount + "   " + data.amountRecoveredDescr) : "";
        return amountSummary.length > 60 ? amountSummary.substring(0, 60) + '...' : amountSummary;
    }

    function convertStatus(data: any){
        let status = handleStatus(data);
        return t(Const.convertTypology(Const.statuses,  status));
    }

    function getReason(reason: any){
        if (reason !== undefined && reason !== "" && (reason.includes("refund.") || reason.includes("payment."))) {
            return t('errors.' + reason);
        } else {
            return reason;
        }
    }
    const defaultValues = {
        paymentType: "",
        amount: "",
        amountType: "",
        status: "",
        dueDate: "",
        invoiceDate: "",
        sapInvoiceNumber: "",
        sapReferenceId: "",
        invoiceNumber: "",
        customerId: "",
        customer: "",
        causal: "",
        ra: "",
        paymentMode: "",
        termsOfPayment: "",
        cardNetwork: "",
        cardTerminalId: "",
        serviceItem: "",
        sddReceipt: "",
        note: "",
        amountRecovered: "",
        id: "",
        modifiedDate: "",
        lastTransactionId: "",
        lastTransactionDate: "",
        maskeredPan: "",
        preauthAmountReleased: "",
        raDate: "",
        vehiclePlateCode: "",
        countyResidence: "",
        bookingDate: "",
        checkoutDmsIdentificationCode: "",
        checkinDmsIdentificationCode: "",
        correlationName: "",
        correlationId: ""
    };


    const { control, setValue, getValues, handleSubmit, watch } = useForm<any>({
        defaultValues: defaultValues,
        resolver: yupResolver(schema)
    });

    const onSubmit: SubmitHandler<any> = (data) => {

    }

    function arrowBack() {
        navigate(`/finance/payments/outstanding-management`)
    }

    return (
        <Mui.Box>
            {showEditNotes &&
                <EditPaymentRequest
                    open={showEditNotes}
                    onClose={toggleEditNote}
                    id={id}
                    note={detail.note}

                />
            }
            {showEditAmount &&
                <EditAmount
                    open={showEditAmount}
                    onClose={toggleEditAmount}
                    id={id}
                    amountRecovered={detail.amountRecovered}
                    amountRecoveredDescr={detail.amountRecoveredDescr}
                />
            }

            <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
                <Tabs value={activeTab} onChange={handleChangeTab}>
                    <Tab label={t('tabs.outstandingDetails')} {...allyProps(0)}/>
                    {<Tab label={t('tabs.customerRADetails')}{...allyProps(0)}/>}
                    <Tab label={t('tabs.customerBookingOwnerDetails')}{...allyProps(0)}/>
                </Tabs>
            </Box>
            <TabPanel value={activeTab} index={0}>
                <Card variant="outlined" sx={{ margin: '16px' }}>

                    <form onSubmit={handleSubmit(onSubmit)}>
                        <CardContent>
                            <Grid container direction={'row'} rowGap={5}>
                                <Grid container item>
                                    <Grid item xs={12}>
                                        <Stack direction={'row'} alignItems={'center'}>
                                            {/*<IconButton color={'primary'} onClick={() => arrowBack()}>*/}
                                            {/*    <ArrowBackRounded />*/}
                                            {/*</IconButton>*/}
                                            <Typography variant={'body2'} color={'primary'} fontWeight={700} textTransform={'uppercase'}>
                                                {t('pages.wizard.form.title-arrow-outstanding', { id: id })}
                                            </Typography>
                                        </Stack>
                                    </Grid>
                                </Grid>
                                <Grid container item spacing={2} alignItems={'center'}>
                                    <Grid item xs={12}>
                                        <Typography variant={'body2'} color={'primary.light'} fontWeight={700} textTransform={'uppercase'}>
                                            {t('pages.wizard.form.title-outstanding')}
                                        </Typography>
                                    </Grid>

                                </Grid>
                                <Grid container item spacing={2} alignItems={'center'}>


                                    <Grid item md={3} sm={12} xs={12}>
                                        <FormTextField
                                            control={control}
                                            name={'paymentType'}
                                            props={{
                                                disabled: true,
                                                label: t('pages.wizard.form.labelPaymentType'),
                                                sx: { width: '100%' },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item md={3} sm={12} xs={12}>
                                        <FormTextField
                                            control={control}
                                            name={'amount'}
                                            props={{
                                                disabled: true,
                                                label: t('pages.wizard.form.labelAmount'),
                                                sx: { width: '100%' },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item md={3} sm={6} xs={12}>
                                        <FormTextField
                                            control={control}
                                            name={'amountType'}
                                            props={{
                                                disabled: true,
                                                label: t('pages.wizard.form.labelAmountType'),
                                                sx: { width: '100%' },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item md={3} sm={6} xs={12}>
                                        <FormTextField
                                            control={control}
                                            name={'status'}
                                            props={{
                                                disabled: true,
                                                label: t('pages.wizard.form.labelStatus'),
                                                sx: { width: '100%' },
                                            }}
                                        />
                                    </Grid>

                                    <Grid item md={3} sm={12} xs={12}>
                                        <FormTextField
                                            control={control}
                                            name={'preauthAmountReleased'}
                                            props={{
                                                disabled: true,
                                                label: t('pages.wizard.form.labelPreAuthReleasedAmount'),
                                                sx: { width: '100%' },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item md={3} sm={12} xs={12}>
                                        <FormDatePicker
                                            control={control}
                                            name={'invoiceDate'}
                                            props={{
                                                disabled: true,
                                                label: t('pages.wizard.form.labelInvoiceDate'),
                                                maxDate: dayjs().toDate(),
                                            }}
                                        />
                                    </Grid>
                                    <Grid item md={3} sm={6} xs={12}>
                                        <FormTextField
                                            control={control}
                                            name={'sapInvoiceNumber'}
                                            props={{
                                                disabled: true,
                                                label: t('pages.wizard.form.labelSapInvoiceNumber'),
                                                sx: { width: '100%' },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item md={3} sm={6} xs={12}>
                                        <FormTextField
                                            control={control}
                                            name={'sapReferenceId'}
                                            props={{
                                                disabled: !(watch('sapReferenceId') === '' || detail?.status === Const.ID_FILTER_EXTERNAL_MANAGEMENT),
                                                label: t('pages.wizard.form.sapReferenceId'),
                                                sx: { width: '100%' },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item md={3} sm={6} xs={12}>
                                        <FormTextField
                                            control={control}
                                            name={'invoiceNumber'}
                                            props={{
                                                disabled: true,
                                                label: t('pages.wizard.form.labelInvoiceNumber'),
                                                sx: { width: '100%' },
                                            }}
                                        />
                                    </Grid>

                                    <Grid container item spacing={2} alignItems={'center'}>
                                        <Grid item md={3} sm={3} xs={6}>
                                            <FormDatePicker
                                                control={control}
                                                name={'dueDate'}
                                                props={{
                                                    disabled: true,
                                                    label: t('pages.wizard.form.labelDueDate'),
                                                    maxDate: dayjs().toDate(),
                                                }}
                                            />
                                        </Grid>
                                        <Grid item md={3} sm={3} xs={6}>
                                            <FormTextField
                                                control={control}
                                                name={'causal'}
                                                props={{
                                                    disabled: true,
                                                    label: t('pages.wizard.form.labelCausal'),
                                                    sx: { width: '100%' },
                                                }}
                                            />
                                        </Grid>
                                        <Grid item md={3} sm={3} xs={6}>
                                            <FormTextField
                                                control={control}
                                                name={'ra'}
                                                props={{
                                                    disabled: true,
                                                    label: t('pages.wizard.form.labelRa'),
                                                    sx: { width: '100%' },
                                                }}
                                            />
                                        </Grid>
                                        <Grid item md={3} sm={3} xs={6}>
                                            <FormDatePicker
                                                control={control}
                                                name={'raDate'}
                                                props={{
                                                    disabled: true,
                                                    label: t('pages.wizard.form.labelRaDate'),
                                                    maxDate: dayjs().toDate(),
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid container item spacing={2} alignItems={'center'}>
                                        <Grid item md={3} sm={6} xs={12}>
                                            <FormTextField
                                                control={control}
                                                name={'customerId'}
                                                props={{
                                                    disabled: true,
                                                    label: t('pages.wizard.form.labelCustomerId'),
                                                    sx: { width: '100%' },
                                                }}
                                            />
                                        </Grid>
                                        <Grid item md={3} sm={6} xs={12}>
                                            <FormTextField
                                                control={control}
                                                name={'customer'}
                                                props={{
                                                    disabled: true,
                                                    label: t('pages.wizard.form.labelCustomer'),
                                                    sx: { width: '100%' },
                                                }}
                                            />
                                        </Grid>
                                        <Grid item md={6} sm={6} xs={12}>
                                            <FormTextField
                                                control={control}
                                                name={'serviceItem'}
                                                props={{
                                                    disabled: true,
                                                    label: t('pages.wizard.form.labelServiceItem'),
                                                    sx: { width: '100%' },
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid item md={3} sm={6} xs={12}>
                                        <FormTextField
                                            control={control}
                                            name={'paymentMode'}
                                            props={{
                                                disabled: true,
                                                label: t('pages.wizard.form.labelPaymentMode'),
                                                sx: { width: '100%' },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item md={3} sm={6} xs={12}>
                                        <FormTextField
                                            control={control}
                                            name={'termsOfPayment'}
                                            props={{
                                                disabled: true,
                                                label: t('pages.wizard.form.labelTermsOfPayment'),
                                                sx: { width: '100%' },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item md={3} sm={6} xs={12}>
                                        <FormTextField
                                            control={control}
                                            name={'cardNetwork'}
                                            props={{
                                                disabled: true,
                                                label: t('pages.wizard.form.labelCardNetwork'),
                                                sx: { width: '100%' },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item md={3} sm={6} xs={12}>
                                        <FormTextField
                                            control={control}
                                            name={'cardTerminalId'}
                                            props={{
                                                disabled: true,
                                                label: t('pages.wizard.form.labelCardTerminalId'),
                                                sx: { width: '100%' },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item md={3} sm={6} xs={12}>
                                        <FormDateTimePicker
                                            control={control}
                                            name={'modifiedDate'}
                                            props={{
                                                disabled: true,
                                                label: t('pages.wizard.form.labelModificationDate'),
                                                maxDate: dayjs().toDate(),
                                            }}
                                        />
                                    </Grid>
                                    <Grid item md={3} sm={6} xs={12}>
                                        <FormTextField
                                            control={control}
                                            name={'lastTransactionId'}
                                            props={{
                                                disabled: true,
                                                label: t('pages.wizard.form.lastTransactionId'),
                                                sx: { width: '100%' },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item md={3} sm={6} xs={12}>
                                        <FormDateTimePicker
                                            control={control}
                                            name={'lastTransactionDate'}
                                            props={{
                                                disabled: true,
                                                label: t('pages.wizard.form.lastTransactionDate'),
                                                maxDate: dayjs().toDate(),
                                            }}
                                        />
                                    </Grid>
                                    <Grid item md={3} sm={6} xs={12}>
                                        <FormTextField
                                            control={control}
                                            name={'maskeredPan'}
                                            props={{
                                                disabled: true,
                                                label: t('pages.wizard.form.maskeredPan'),
                                                sx: { width: '100%' },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item md={3} sm={6} xs={12}>
                                        <FormTextField
                                            control={control}
                                            name={'id'}
                                            props={{
                                                disabled: true,
                                                label: t('pages.wizard.form.labelId'),
                                                sx: { width: '100%' },
                                            }}
                                        />
                                    </Grid>


                                    <Grid item md={3} sm={6} xs={12}>
                                        <FormTextField
                                            control={control}
                                            name={'vehiclePlateCode'}
                                            props={{
                                                disabled: true,
                                                label: t('pages.wizard.form.labelPlate'),
                                                sx: { width: '100%' },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item md={3} sm={6} xs={12}>
                                        <FormTextField
                                            control={control}
                                            name={'countyResidence'}
                                            props={{
                                                disabled: true,
                                                label: t('pages.wizard.form.labelCountyResidence'),
                                                sx: { width: '100%' },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item md={3} sm={6} xs={12}>
                                        <FormDatePicker
                                            control={control}
                                            name={'bookingDate'}
                                            props={{
                                                disabled: true,
                                                label: t('pages.wizard.form.labelBookingDate'),
                                                maxDate: dayjs().toDate(),
                                            }}
                                        />
                                    </Grid>
                                    <Grid item md={3} sm={6} xs={12}>
                                        <FormTextField
                                            control={control}
                                            name={'checkoutDmsIdentificationCode'}
                                            props={{
                                                disabled: true,
                                                label: t('pages.wizard.form.labelCheckoutDmsIdentificationCode'),
                                                sx: { width: '100%' },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item md={3} sm={6} xs={12}>
                                        <FormTextField
                                            control={control}
                                            name={'checkinDmsIdentificationCode'}
                                            props={{
                                                disabled: true,
                                                label: t('pages.wizard.form.labelCheckinDmsIdentificationCode'),
                                                sx: { width: '100%' },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item md={3} sm={6} xs={12}>
                                        <FormTextField
                                            control={control}
                                            name={'correlationName'}
                                            props={{
                                                disabled: true,
                                                label: t('pages.wizard.form.correlationName'),
                                                sx: { width: '100%' },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item md={3} sm={6} xs={12}>
                                        <FormTextField
                                            control={control}
                                            name={'correlationId'}
                                            props={{
                                                disabled: true,
                                                label: t('pages.wizard.form.correlationId'),
                                                sx: { width: '100%' },
                                            }}
                                        />
                                    </Grid>

                                    <Grid container item spacing={2} alignItems={'center'}>
                                        {hasScope(OUTSTANDING_ADD_NOTE) && (
                                            <>
                                                <Grid item md={5.5} sm={5.5} xs={5.5}>
                                                    <FormTextField
                                                        control={control}
                                                        name={'note'}
                                                        props={{
                                                            disabled: true,
                                                            label: t('pages.wizard.form.note'),
                                                            sx: { width: '100%' },
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item md={0.5} sm={0.5} xs={0.5}>
                                                    <IconButton color="accent" onClick={() => openEditModalNotes()}>
                                                        <EditIcon />
                                                    </IconButton>
                                                </Grid>
                                            </>
                                        )}
                                        {hasScope(OUTSTANDING_ADD_AMOUNT) && (
                                            <>
                                                <Grid item md={5.5} sm={5.5} xs={5.5}>
                                                    <FormTextField
                                                        control={control}
                                                        name={'amountRecovered'}
                                                        props={{
                                                            disabled: true,
                                                            label: t('pages.wizard.form.amount'),
                                                            sx: { width: '100%' },
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item md={0.5} sm={0.5} xs={0.5}>
                                                    <IconButton color="accent" onClick={() => openEditModalAmount()}>
                                                        <EditIcon />
                                                    </IconButton>
                                                </Grid>
                                            </>
                                        )}
                                    </Grid>
                                </Grid>
                                <Grid container item spacing={2} alignItems={'center'}>
                                    <UnpaidHistory />
                                </Grid>
                            </Grid>
                        </CardContent>
                        <CardActions sx={{ justifyContent: 'flex-end' }}>
                            {showCardActions()}
                        </CardActions>
                    </form>
                    <FinanceDialog open={openModal}
                                   onClose={() => setOpenModal(false)}
                                   callback={() => {
                                       putRequest('accounted');
                                   }}
                                   titleIcon={<InfoRounded style={{color: 'accent.main'}}/>}
                                   title={t('pages.wizard.sapReferenceIdModal.title')}
                                   content={t('pages.wizard.sapReferenceIdModal.content')}
                                   confirmButton={t('pages.wizard.sapReferenceIdModal.actionButtons.confirm')}
                                   cancelButton={t('pages.wizard.sapReferenceIdModal.actionButtons.cancel')}
                    />
                </Card>
            </TabPanel>
            {detail?.customerRa &&
                <TabPanel value={activeTab} index={1}>
                    <CustomerRADetails customerRADetails={detail?.customerRa ? detail.customerRa : detail}/>
                </TabPanel>
            }
            {detail?.customerBooking &&
                <TabPanel value={activeTab} index={2}>
                    <CustomerBookingDetails
                        customerBookingDetails={detail?.customerBooking ? detail.customerBooking : detail}/>
                </TabPanel>
            }

        </Mui.Box>
    );

    function openEditModalNotes(): void {
        toggleEditNote();
    }
    function openEditModalAmount(): void {
        toggleEditAmount();
    }

    function showCardActions() {
        let status = detail?.status;
        let alreadySentToSap = detail?.alreadySentToSap;

        let showOutstanding = !alreadySentToSap && (status === Const.ID_FILTER_SUSPENDED || status === Const.ID_FILTER_EXTERNAL_MANAGEMENT || status === Const.ID_FILTER_ACCOUNTED) && hasScope(OUTSTANDING_STATUS)
        let showSuspended   = !alreadySentToSap && (status === Const.ID_FILTER_OUTSTANDING || status === Const.ID_FILTER_EXTERNAL_MANAGEMENT) && hasScope(OUTSTANDING_SUSPEND)
        let showGE          = !alreadySentToSap && (status === Const.ID_FILTER_OUTSTANDING || status === Const.ID_FILTER_SUSPENDED || status === Const.ID_FILTER_ACCOUNTED) && hasScope(OUTSTANDING_EXTERNAL_MANAGEMENT)
        let showClose       = !alreadySentToSap && (status === Const.ID_FILTER_OUTSTANDING || status === Const.ID_FILTER_SUSPENDED || status === Const.ID_FILTER_EXTERNAL_MANAGEMENT) && hasScope(OUTSTANDING_ACCOUNTED)

        return (
            <>
                { showOutstanding &&
                    <Button color={"accent"} size={"small"} onClick={() => putRequest('outstanding')}>{t('pages.wizard.buttons.outstanding')}</Button>}
                { showSuspended &&
                    <Button color={"accent"} size={"small"} onClick={() => putRequest('suspend')}>{t('pages.wizard.buttons.suspend')}</Button>}
                { showGE &&
                    <Button color={"accent"} size={"small"} onClick={() => putRequest('external-management')}>{t('pages.wizard.buttons.external-management')}</Button>}
                { showClose &&
                    <Button color={"accent"} size={"small"} onClick={() => (watch('sapReferenceId') == '' && status === Const.ID_FILTER_EXTERNAL_MANAGEMENT) ? setOpenModal(true) : putRequest('accounted')}>{t('pages.wizard.buttons.close')}</Button>}
            </>
        );
    }

    function putRequest(type: string) {
        let sapReferenceId = type == 'accounted' ? watch('sapReferenceId') : null;
        putPaymentsRequestsById(detail?.id, type, sapReferenceId, detail?.dealer, detail?.legalEntity).then(() => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('snackbar.success-change-state') + (type==="accounted" ?  " \n "+ t('snackbar.manualAccountedStateMessage') : ""),
                        type: snackbarTypes.success,
                        show: true,
                        autoClose: true
                    }
                }
            });
            setUpdating(true);
            window.scrollTo(0,0)
        }).catch(() => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('snackbar.error-change-state'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });
        });
    }

}

export default OutstandingManagementDetailWizard;

